/**
 * host — the Host capability surface as connectable SLOTS (registry id
 * `host`). It has no business UI: a blueprint wires
 * `<comp>.<signal> → host.<slot>` and the runtime calls the matching host
 * callback. Drawn dashed in the signal/slot graph, and invisible on the page.
 *
 * When a blueprint places `host` as an actual node (rare — it is normally only
 * a connection target), it renders a compact trace strip so the studio can see
 * which host slots this page is wired to.
 *
 * THIS FILE IS ALSO THE DECLARED SURFACE. `HOST_SLOTS` is the one place the web
 * kit writes down *which* slots exist and *what payload* each one takes; the
 * registry's `host` pseudo-component mirrors it, the Flutter kit's
 * `ViewIrHost` / `ViewIrShellHost` mirror it again, and the studio's mock host
 * implements it. A slot that is not here is not a slot.
 *
 * The `logData awaitConfirm` ladder is the same surface's other half and lives
 * in `hostConfirm.js` — only because a `.jsx` file that exports a component may
 * not also export functions.
 */

/**
 * Every `host.*` slot, with the payload it declares.
 *
 * The three in the middle block are the ones prod's WebView bundles reach for
 * through `postMessage` and the native kit had no word for:
 *
 *   reloadData       prod `bridge('reload_widget_data',{kind})` — the Retry on
 *                    an error pane. Without it every blueprint's Retry is
 *                    kit-internal and bounded, so a feed that stays broken has
 *                    no door out but the main chat (art-studio + doc-digitizer
 *                    + nutritionist all say so in their own notes).
 *   setSubview       prod `bridge('widget_subview',{active})` — "I entered an
 *                    in-bundle sub-view": the host routes its back chevron into
 *                    the widget and hides the bottom bar, so a
 *                    `PageShell layout:"subpage"` does not have to draw a
 *                    second back chevron of its own.
 *   setSessionBadge  prod `postBridge('session_badge_target',{log_card_id,
 *                    session_id})` — hand the in-widget clock over to the host
 *                    chrome. Its inbound half is the `user.host_session_badge`
 *                    context path (see `context.js`): the host says "I am
 *                    already showing a badge", the widget hides its own control
 *                    with a `when`, and tells the host which session it means.
 *                    Ship one half only and the user sees two clocks.
 *
 * `openSession` declares `prompt` because it always carried one in practice —
 * prod's data_view Generate button posts `open_session` with `data-prompt`
 * rebuilt on every chip tap — and a payload map that cannot name it forces the
 * blueprint into `openMainSession`, which navigates somewhere else entirely.
 *
 *   attachItem       ADD PHOTOS TO A ROW THAT ALREADY EXISTS — the host's own
 *                    append lane (prod's `attach_item_image`), Flutter-only
 *                    until now and therefore unnameable from a blueprint:
 *                    picker → upload → PATCH `/data/{entry_id} {add_images}`,
 *                    server-side, none of it the widget's to hand-roll.
 *                    `dispatch` (default true) says whether the append also
 *                    CONTINUES the row's source session; `dispatch:false` is
 *                    the append-only half — the photos land and nothing runs,
 *                    which is what a "+ add page" means. Without it a widget
 *                    had to fake the lane out of `pickMedia` + a chained
 *                    `host.hostResult`, and a BARE `hostResult` wire matches
 *                    every answering op, so the fake also carried a hazard.
 *
 * THE LONG-PRESS PAIR. `itemOptions` is what a long press opens — the host's
 * options sheet (Delete always, Save when the payload carries an `image_url`
 * or `video_url`),
 * and it takes BOTH shapes a cell can carry: a logged row (`item_id`) and an
 * in-flight capture (`grid_id`/`session_id`, whose Delete drops the session).
 * `crudRemove` is the delete PRIMITIVE that sheet's Delete calls; it is no
 * longer a blueprint target for a long press, because a gesture that deletes
 * with no confirmation and no options is the bug both kits had.
 *
 * `openItem` spells `...page_args` for the same reason: the host opens the
 * detail page with the WHOLE payload as that page's one-shot `page_args`, so a
 * home card can say which view it means (`{item_id, tab}`) instead of landing
 * on the page's default and making the user find the tab again. `item_id` is
 * still the focus; every other key rides along.
 */
export const HOST_SLOTS = Object.freeze({
  openItem: '{item_id,...page_args}',
  openPage: '{page_file,args}',
  openSession: '{session_id,log_card_id?,prompt?}',
  openMainSession: '{prompt?}',
  openCapture: '{prompt,images,target_tab,open_session?,kind?}',
  // `cascade` (app 1.1.4) closes the opener too, up to the widget's root page.
  closePage: '{action,toast,cascade?}',
  logData: '{payload,log_card_id,awaitConfirm?}',
  pickMedia: '{max}',
  crudRemove: '{item_id|grid_id,image_index?}',
  itemOptions: '{item_id|grid_id,session_id?,image_url?,video_url?}',
  imageTap: '{src,image_url?,index?,images?}',
  attachItem: '{item_id,max?,dispatch?}',
  reloadData: '{kind?}',
  setSubview: '{active}',
  setSessionBadge: '{session_id,log_card_id}',
  download: '{url,item_id?,filename?}',
  openUrl: '{url}',
  copyText: '{text}',
  declareNavAction: '{action}',
  // A server-view page's allowlisted action (README §4.7); answered by the runtime.
  viewAction: '{action,args?,view?,optimistic?}',
  // A bottom snackbar whose action runs a view action (app 1.1.4); drawn by the runtime.
  showSnackbar: '{message,duration_ms?,undo_until?,action?}',
});

export const SLOTS = Object.freeze(Object.keys(HOST_SLOTS));

export default function HostAdapter({ props = {} }) {
  if (!props.visible) return null;
  return (
    <div style={{
      border: '1px dashed var(--line)', borderRadius: 'var(--r-sm)',
      padding: 'var(--sp-2)', fontSize: 'var(--t-xs)', color: 'var(--text-3)',
      fontFamily: 'var(--font-mono)', lineHeight: 1.6,
    }}>
      host · {SLOTS.join(' · ')}
    </div>
  );
}
